import dayjs from "dayjs";
import {
  dateRangePlaceHolder,
  genericTypeMapper,
  overShortDateLimit,
  freightClaimDateLimit,
  userRoles,
  ITEM_TYPE_TAG_OPTIONS_MAPPER
} from "src/config/constants/genericConstants";
import stringToNumber from "./stringToNumber";
import { v4 as uuidv4 } from "uuid";
import {
  getDaysAfterDate,
  isTodayAfterReferenceDate,
  getMaximumDate as maximumDate,
  getMinimumDate as minimumDate
} from "./getDates";
import { flattenSubclassListType } from "src/pages/Dashboard/interface";

type Order = "ASC" | "DESC";

export const getSelectedDate = (fromDate: string, toDate: string) => {
  return fromDate && toDate ? `${fromDate} - ${toDate}` : dateRangePlaceHolder;
};

export const DatePickerSubmitHandler = (from: Date, to: Date) => {
  const fromDate = dayjs(from).format("MM/DD/YY");

  const toDate = dayjs(to).format("MM/DD/YY");

  return { fromDate, toDate };
};

export const formatNumber = (q: any) => {
  return q?.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
};

export const getInitialArrayValue = (data: any[]) => {
  return data || [];
};

export const getMinimumDate = (value: number, type: any) => {
  return dayjs(new Date()).subtract(value, type);
};

export const daysBetween = (date1String: string, date2String: string) => {
  const d1 = new Date(date1String);
  const d2 = new Date(date2String);
  return Number(Number(d2) - Number(d1)) / (1000 * 3600 * 24);
};

export const numberWithCommas = (x: any) => {
  return x?.toLocaleString("en-US");
};

export const checkApprovalUser = (user: string) => {
  if (user === "Inventory Accounting User" || user === "Logistics User") {
    return true;
  }
  return false;
};

export const checkIoUniversalUser = (user: string) => {
  if (user === "Independent Operator" || user === "Universal Operator") {
    return true;
  }
  return false;
};

export const isNumeric = (num: any) =>
  (typeof num === "number" || typeof num === "string") && !isNaN(num as number);

export const getDaysInTheMonth = (year: number, month: number) =>
  new Date(year, month, 0).getDate();

export const getDateUsingMonths = (fromDate: any, months: number) => {
  const resultedDate = new Date(fromDate);
  resultedDate.setDate(1);
  resultedDate.setMonth(resultedDate.getMonth() + months);
  resultedDate.setDate(
    Math.min(
      fromDate.getDate(),
      getDaysInTheMonth(resultedDate.getFullYear(), resultedDate.getMonth() + 1)
    )
  );
  return resultedDate;
};

export const getAverage = (data: number[]) => {
  let computedValue =
    data.reduce((a: number, b: number) => a + b, 0) / data.length;
  computedValue = stringToNumber(
    computedValue.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })
  );
  return computedValue;
};

export const getDecimalPointNumber = (number: string, decimalPoint: number) => {
  let fixedValue = number;
  const hasDecimal = number.indexOf(".");
  if (hasDecimal !== -1) {
    if (hasDecimal >= number.length - decimalPoint) {
      fixedValue = number;
    } else {
      fixedValue = number.substring(0, hasDecimal + (decimalPoint + 1));
    }
  }
  return fixedValue;
};

export const convertStringToCurrencyWithDecimal = (fixedValue: string) => {
  const numericValue = stringToNumber(fixedValue);

  if (isNonNumeric(fixedValue)) {
    return handleNonNumericValue(fixedValue);
  }

  return handleNumericValue(numericValue);
};

// Check if the value is not a number
const isNonNumeric = (value: string): boolean => {
  return isNaN(stringToNumber(value));
};

// Handle non-numeric values
const handleNonNumericValue = (value: string): string => {
  return value + "."; // Appending a period for non-numeric values
};

// Handle numeric values
export const handleNumericValue = (numericValue: number): string => {
  return numericValue?.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const convertStringToCurrencyWithDecimalNegative = (
  valueIsNotANumber: boolean,
  fixedValue: string
) => {
  return stringToNumberWithNegative(fixedValue).toLocaleString("en-US");
};

export const getDollorValue = (value: any) => {
  const convertedValue = typeof value !== "string" ? String(value) : value;
  const computedValue = convertedValue.startsWith("-")
    ? `-$${convertedValue.slice(1, convertedValue?.length)}`
    : `$${convertedValue}`;
  return computedValue;
};

export const stringToNumberWithNegative = (value: string) => {
  if (value === "0-" || value === "-0") return Number(-0);
  return value.replace(/[^0-9-.]+/g, "");
};
export default stringToNumberWithNegative;

export const getSortOrder = (
  selectedSortParam: string,
  currentSortParam: string,
  sortOrder: Order
) => {
  let sortOrderValue: Order = "DESC";
  if (selectedSortParam === currentSortParam && sortOrder === "DESC") {
    sortOrderValue = "ASC";
  }
  return sortOrderValue;
};

export const trimZeros = (text: string) => {
  if (typeof text !== "string") return text;

  return text.replace(/^0+/, "");
};

export const toggleSortOrder = (sortOrder: "ASC" | "DESC"): "ASC" | "DESC" => {
  if (sortOrder === "ASC") return "DESC";
  return "ASC";
};

export const formatToCurrency = (value: number, min = 2, max = 2) => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: min,
    maximumFractionDigits: max
  });
  return formatter.format(value || 0);
};

export const sortList = (listToSort: any, key: string, isNumeric?: boolean) => {
  return listToSort?.sort((a: any, b: any) => {
    if (isNumeric) {
      return a[key] - b[key];
    }
    return a[key] > b[key] ? 1 : -1;
  });
};

export const getSlicedDate = (date: string) => {
  return date?.slice(0, 10);
};

export const getNumericValue = (value: string) => {
  const cleanedValue = value?.replace(/^\D+/g, "")?.replace(",", "");
  return Number(getFirstDefined(cleanedValue, "0"));
};

export const decimal = (num: any) => {
  if (!num) return "-";
  // Convert input string to a number and store as a variable.
  const value = Number(num);
  // Split the input string into two arrays containing integers/decimals
  const res = num?.split(".");
  // only add decimal if number of decimal digits more than or equal to 1 .
  if (res[1] && (!res[1]?.startsWith("00") || res[1]?.length > 2)) {
    // Set the number to two decimal places
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }
  // Return updated or original number.
  return value;
};

export const formatToPercentage = (value: any) => {
  if (!value) return "-";
  return value + "%";
};

export const mapAlternateValue = (key: string) => genericTypeMapper[key] || key;

export const isDsdOrWt = (type: string) => {
  return (
    type?.toUpperCase() === "DSD" ||
    type?.toUpperCase() === "TRANSFER" ||
    type?.toUpperCase() === "CREDIT"
  );
};

type ReportType = {
  id: string;
  type?: string;
  reportName: string;
  reportDescripton?: string;
  autoDeletionInterval?: string;
  categories?: string[];
  externalReport?: boolean;
  reportLink?: null | string;
  defaultGoal?: string;
  isFavourite?: boolean;
  lastGeneratedDate?: null | string;
};

export const getReportIdByName = (
  reports: ReportType[],
  name: string
): string => {
  const report = reports?.find((report: any) =>
    report?.reportName?.toLowerCase().includes(name)
  );
  return report?.id || "";
};

export const getComponentKey = (): any => {
  const uniqueId = uuidv4();
  return uniqueId;
};

/*
  get order or recived values
  @param quantity
  @param packOrSize
  @param isRandomWeight
*/
export const getQuantityInEaches = (
  quantity: string,
  packOrSize: string,
  isRandomWeight: boolean
): number | string => {
  if (quantity === "" || quantity === null || typeof packOrSize !== "string") {
    return "-";
  }

  if (isRandomWeight) {
    return +quantity || 0;
  }

  const [size] = packOrSize.split("/");
  const sizeValue = size?.replace(/[^0-9-.]+/g, "") || 0;

  return +(((+quantity || 0) * +sizeValue || 0).toFixed(2) || 0);
};

export const getItem = <T>(items: T[], uniqueId: string): T => {
  return items?.find((poItem: any) => poItem.uniqueId === uniqueId);
};

export const findMinMaxDates = (
  dateArray: string[]
): [dayjs.Dayjs | null, dayjs.Dayjs | null] => {
  if (!dateArray || dateArray.length === 0) {
    return [null, null]; // If the array is empty, return [null, null]
  }

  // Convert the date strings to dayjs objects
  const dateObjects = dateArray.map((date) => dayjs(date));

  // Find the smallest and largest dates
  const minDate = minimumDate(dateObjects);
  const maxDate = maximumDate(dateObjects);

  return [minDate, maxDate];
};

export const filterDuplicateAndGetLatestDate = (
  objects: any,
  attributeToCheck: string,
  dateAttribute: string
) => {
  // Create a map to store unique objects based on the attributeToCheck
  const uniqueObjectsMap = new Map();

  // Iterate over each object in the array
  objects?.forEach((obj: { [x: string]: string | number | Date }) => {
    const attributeValue = obj[attributeToCheck];
    // Check if the map already has an object with the same attribute value
    if (uniqueObjectsMap?.has(attributeValue)) {
      // If the current object has a later date, update the map with this object
      if (
        new Date(obj[dateAttribute]) >
        new Date(uniqueObjectsMap?.get(attributeValue)[dateAttribute])
      ) {
        uniqueObjectsMap.set(attributeValue, obj);
      }
    } else {
      // If the attribute value is not in the map, add the current object
      uniqueObjectsMap.set(attributeValue, obj);
    }
  });
  // Return an array of unique objects
  return Array.from(uniqueObjectsMap?.values());
};

export const isInventoryAccountingUser = (userRole: string) =>
  userRole === "Inventory Accounting User";

export const isLogisticsUser = (userRole: string) =>
  userRole === "Logistics User";

export const isIndependentOperator = (userRole: string) =>
  userRole === "Independent Operator";

export const isUniversalOperator = (userRole: string) =>
  userRole === "Universal Operator";

export const isSupportUser = (userRole: string) => userRole === "Support";

export const getFormatedStoreName = (store: any) => {
  const storeName = store?.storeName;
  const storeId = getFirstDefined(store?.storeId, store?.storeID);
  return [storeName, storeId].every(Boolean)
    ? [storeName.split(",")?.[0], storeId].join(" - ")
    : "-";
};
export const isSupplyingStoreUser = (storeId: string, supplierId: string) =>
  storeId?.toString().replace(/^0+/, "") ===
  supplierId?.toString().replace(/^0+/, "");

export const getStoreName = (storeId: string, storeList: any[]) => {
  const store = storeList?.find((store) => store.storeId === storeId);
  return store ? getFormatedStoreName(store) : "-";
};

export const getBatchedExportPayload = ({
  recordsCount,
  recordsPerFile,
  payload,
  pageKey,
  fileNamePrefix
}: {
  recordsCount: number | string;
  recordsPerFile: number;
  payload: any;
  pageKey: string;
  fileNamePrefix: string;
}) => {
  const totalBatches = Math.ceil(Number(recordsCount) / recordsPerFile);

  const batchedPayload = [];

  for (let batch = 0; batch < totalBatches; batch++) {
    batchedPayload.push({
      ...payload,
      [pageKey]: batch + 1,
      fileName: fileNamePrefix + ` Part ${batch + 1}`
    });
  }

  return batchedPayload;
};

export const formatCurrencyOrNumber = (price: any, min = 2, max = 2) => {
  let negativeValue: boolean = false;
  let dollarValue: boolean = false;
  let updatedPrice: string | number = `${getFirstDefined(price, "")}`;
  if (updatedPrice.includes("$")) {
    dollarValue = true;
    updatedPrice = updatedPrice.replace("$", "");
  }
  if (Math.sign(Number(updatedPrice)) === -1 || updatedPrice.startsWith("-")) {
    negativeValue = true;
    updatedPrice = updatedPrice.replaceAll("-", "");
  }
  if (dollarValue) {
    updatedPrice = formatToCurrency(stringToNumber(updatedPrice), min, max);
  } else {
    updatedPrice = `${roundNumber(updatedPrice)}`;
  }
  if (negativeValue && stringToNumber(updatedPrice) > 0) {
    updatedPrice = `-${updatedPrice}`;
  }
  return updatedPrice;
};
export const concateNameIdToColumn = (
  data: { id: string; name: string }[],
  columnName = "name"
) =>
  data
    ?.map((eachData: any) => ({
      ...eachData,
      [columnName]: [Number(eachData?.id), eachData?.name]
        .filter(Boolean)
        .join(" - ")
    }))
    .sort((a: any, b: any) =>
      a[columnName].localeCompare(b[columnName], undefined, {
        numeric: true,
        sensitivity: "accent"
      })
    );

export const roundNumber = (
  number: any,
  minFractionDigit = 2,
  maxfractionDigit = 2
) => {
  if (isNaN(Number(number))) return 0;
  const result = Number(number).toLocaleString("en", {
    maximumFractionDigits: maxfractionDigit,
    minimumFractionDigits: minFractionDigit,
    useGrouping: false
  });
  return Number(result);
};

export const isTodayWithinDateLimitForStoreCredit = (
  receivedDate: any,
  limitDays: number
) => {
  const limitDate = getDaysAfterDate(receivedDate, limitDays, "YYYY-MM-DD");

  return !isTodayAfterReferenceDate(limitDate);
};

export const isTodayWithinDateLimitForOs = (receivedDate: any) =>
  isTodayWithinDateLimitForStoreCredit(receivedDate, overShortDateLimit);

export const isTodayWithinDateLimitForFc = (receivedDate: any) =>
  isTodayWithinDateLimitForStoreCredit(receivedDate, freightClaimDateLimit);

export const setDepartments = (
  flatternSubClass: flattenSubclassListType[],
  filterObj: any,
  type: "classId" | "subClassId"
) => {
  const displayDepartments: string[] = [];
  flatternSubClass.forEach((subClassObj) => {
    if (filterObj.includes(subClassObj[type])) {
      displayDepartments.push(subClassObj.departmentId);
    }
  });
  return displayDepartments;
};

export const shouldHighlightReceivedQtyDSD = (dsdItem: any) => {
  const receivedNum = Number(dsdItem.received);
  const orderedNum = Number(dsdItem.ordered);

  // If ordered value less than or equal to 0 or NaN
  if (isNaN(orderedNum) || orderedNum <= 0) {
    return false;
  }

  return receivedNum !== orderedNum;
};

export const addAsterisk = (name: string, condition: boolean): string => {
  return (name + (condition ? " *" : ""))?.trim();
};

export const checkAllValues = <T>(
  data: T[],
  property: keyof T,
  condition: (value: any) => boolean
): boolean => {
  return data?.every((item) => condition(item[property]));
};

// Helper function to map sort parameters
export const mapSortParam = (sortParam: string): string => {
  const sortMapping: { [key: string]: string } = {
    docWithoutLink: "doc",
    upcLink: "upc",
    itemWithoutLink: "itemId",
    itemWithInfoWithoutLInk: "itemId",
    descriptionWithBadge: "description"
  };
  return sortMapping[sortParam] || sortParam;
};

export const displayEmptyOrDashConditionally = (
  value: string | number,
  displayEmptyToDash: boolean
) => {
  if (!value && !displayEmptyToDash) value = "-";
  if (!value && displayEmptyToDash) value = "";
  return value;
};

export const getFirstDefined = <T>(
  primary: T | null | undefined,
  fallback: T
) => primary || fallback;

export const extractNumbersOnly = (value: string | number): number => {
  if (typeof value === "number") {
    return extractNumbersOnly(value.toString());
  }
  return Number(value.replace(/[^\d]+/g, ""));
};

export const formatDecimalsByValue = (
  value: number,
  minDecimal: number = 0,
  maxDecimal: number = 2
) => value.toFixed(Number(value) > 0 ? maxDecimal : minDecimal);

export const capitalizeFirstLetter = (string: string) =>
  string?.toLowerCase().replace(/^./, string?.[0]?.toUpperCase());

export const openUrlInNewTabWithAnchor = (url: string) => {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  anchor.click();
};

export const getRejectionOrClaimReasons = (
  appConfiguration: any,
  reasonOrClaimKeyMapper: { [key: string]: string },
  receivingType: string
) => {
  const reasonKey = reasonOrClaimKeyMapper[receivingType?.toUpperCase()];
  const reasons = appConfiguration?.[reasonKey] || [];
  return reasons?.map((reason: any) => reason?.ReasonDescription);
};

export const isUniversalUser = (role: string) => userRoles.UO === role;

export const getItemTypes = (item: { tags: string[] }) => {
  const itemTypes = new Set<string>();

  Object.entries(ITEM_TYPE_TAG_OPTIONS_MAPPER).forEach(([type, tagIds]) => {
    if (item.tags?.some((tag) => tagIds.includes(tag))) {
      itemTypes.add(type.toUpperCase());
    }
  });

  return [...itemTypes];
};

/**
 * Reads rowData[key] and returns an inclusive numeric max when the value is present
 * and parses to a finite number >= 0. Otherwise null (caller applies no row-driven cap).
 */
export const resolveNumericMaxFromRowKey = (
  rowData: Record<string, unknown> | null | undefined,
  key: string | undefined
): number | null => {
  if (!key) return null;
  const raw = rowData?.[key];
  if (raw === null || raw === undefined || raw === "") {
    return null;
  }
  if (typeof raw === "string" && raw.trim() === "") {
    return null;
  }
  const n =
    typeof raw === "number" ? raw : stringToNumber(raw as string | number);
  if (Number.isNaN(n) || n < 0) {
    return null;
  }
  return n;
};
